// src/pages/Results.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchRecentTests } from "../api";
import RingChart from "../components/RingChart";

export default function Results() {
  const navigate  = useNavigate();
  const [tests,   setTests]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  // Also check if there's a practice result in sessionStorage
  const practiceResult = (() => {
    try { return JSON.parse(sessionStorage.getItem("practiceResult") || "null"); } catch { return null; }
  })();

  // Check if there's a test result in sessionStorage
  const testResult = (() => {
    try { return JSON.parse(sessionStorage.getItem("testResult") || "null"); } catch { return null; }
  })();

  useEffect(() => {
    fetchRecentTests()
      .then(d  => { setTests(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  if (loading) return <div className="loading"><div className="spinner" /><span>Loading results…</span></div>;

  // Show last test result if available
  const r = testResult;

  return (
    <div>
      <h1 className="page-title">Results</h1>
      <p className="page-sub">Your test history and performance insights</p>

      {error && <div className="error-box">{error}</div>}

      {/* Latest test result */}
      {r && (
        <>
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="card-title">Latest test result</div>
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <RingChart pct={Math.round((r.accuracy || 0) * 100)} size={80} strokeWidth={7} />
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
                  {Math.round((r.accuracy || 0) * 100) >= 75 ? "Excellent work! 🎉" :
                   Math.round((r.accuracy || 0) * 100) >= 50 ? "Good effort! 💪" : "Keep practising! 🔄"}
                </div>
                <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                  {r.correct}/{r.totalQuestions} correct &nbsp;·&nbsp; Score: {typeof r.score === "number" ? r.score.toFixed(2) : r.score} marks
                </div>
              </div>
            </div>

            <div className="result-stats" style={{ marginTop: 16 }}>
              {[["Correct", r.correct, "var(--green)"], ["Wrong", r.wrong, "var(--red)"], ["Unattempted", r.totalQuestions - r.attempted, "var(--text-secondary)"]].map(([l, v, c]) => (
                <div className="rs" key={l}>
                  <div className="rs-val" style={{ color: c }}>{v}</div>
                  <div className="rs-lbl">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* What the system learned */}
          {r.topicBreakdown && r.topicBreakdown.length > 0 && (
            <div className="learned-card">
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>What the system learned</div>
              {r.topicBreakdown.slice(0, 3).map((t, i) => {
                const a        = t.total > 0 ? t.correct / t.total : 0;
                const newDiff  = a >= 0.70 ? "hard" : a >= 0.40 ? "medium" : "easy";
                const diffTag  = newDiff === "easy" ? "tag-easy" : newDiff === "medium" ? "tag-medium" : "tag-hard";
                return (
                  <div key={i} style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.65, marginBottom: 6 }}>
                    Your accuracy on <strong style={{ color: "var(--text-primary)" }}>{t.topic}</strong> was{" "}
                    <strong>{Math.round(a * 100)}%</strong>. Engine will now serve{" "}
                    <span className={`tag ${diffTag}`}>{newDiff}</span> questions next session.
                  </div>
                );
              })}
              <div className="next-up-box">
                ⚡ <span>Continue: <strong>Adaptive Practice</strong> is ready with updated difficulty levels</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* All recent tests */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Test history</div>
        {tests.length === 0 ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 14 }}>No tests taken yet.</p>
            <button className="btn btn-dark btn-sm" onClick={() => navigate("/test")}>Take your first test</button>
          </div>
        ) : (
          tests.map((t, i) => (
            <div key={i} className="topic-row">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <RingChart pct={Math.round((t.accuracy || 0) * 100)} size={40} strokeWidth={4} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, textTransform: "capitalize" }}>
                    {t.testType.replace("-", " ")} test
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                    {t.subject || "All subjects"} · {new Date(t.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.accuracy >= 0.7 ? "var(--green)" : t.accuracy >= 0.5 ? "var(--amber)" : "var(--red)" }}>
                  {Math.round((t.accuracy || 0) * 100)}%
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{t.correct} correct</div>
              </div>
            </div>
          ))
        )}
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn btn-dark" onClick={() => navigate("/test")}>Take new test</button>
        <button className="btn" onClick={() => navigate("/practice")}>Continue practice</button>
      </div>
    </div>
  );
}
